import { Component, Prop, Host, Element, h } from "@stencil/core";
import { dispatchEvent } from "../../utils/dispatchEvent";
import { createStaticFilter } from "../../utils/staticFilter/createStaticFilter";

@Component({
    tag: "ux-iws-data-controller-product-overview",
    styleUrl: "data-controller-product-overview.scss",
})
export class DataControllerProductOverview {
    @Element() element: HTMLUxIwsDataControllerProductOverviewElement;

    /** the url to which the request will be made for the results */
    @Prop() src: string = null;
    /** an optional text that indicated the loading state */
    @Prop() loadingText: string = "";
    /** the css selector of the ux-filter element that does the results filtering */
    @Prop() filterSelector: string = null;
    /** the css selector of the element that contains the number of the results */
    @Prop() counterSelector: string = null;
    /** optional form data element to be passed in the POST request */
    @Prop() formDataAttribute = null;
    /** filter attribute identifier for the boxes and tags */
    @Prop() filterAttributeIdentifier: string = "product";

    private baseClass = "ux-iws-data-controller-product-overview";
    private itemHiddenAttribute = "data-ux-product-hidden";
    private itemFilterAttribute: string = `data-ux-${this.filterAttributeIdentifier}-filter`;
    private visibleItemsSelector = `*[${this.itemFilterAttribute}]:not([${this.itemHiddenAttribute}])`;
    private contactTeaserSelector = "ux-iws-nb-teaser-contact";
    private resultsContainer: HTMLElement = null;
    private filterElement: HTMLUxFilterElement = null;
    private contactTemplateHtml: string = "";

    async componentWillLoad() {
        this.resultsContainer = this.element.querySelector(
            "*[slot='results-container']"
        );
        this.contactTemplateHtml =
            this.element
                .querySelector("script[slot='contact-template']")
                ?.innerHTML.trim() || "";
        this.filterElement = document.querySelector(this.filterSelector);
        const { store } = createStaticFilter({
            counterSelector: this.counterSelector,
            filterElement: this.filterElement,
            itemFilterAttribute: this.itemFilterAttribute,
            itemsContainerElement: this.resultsContainer,
            itemHiddenAttribute: this.itemHiddenAttribute,
        });
        store.onChange("selectedFilters", (selectedFilters) => {
            this.updateHighlightedTags(selectedFilters);
            this.updateContactTeasers();
        });
        store.onChange("visibleItemsCount", () => {
            dispatchEvent("resize");
        });
    }

    private updateHighlightedTags(selectedFilters: string[]) {
        this.resultsContainer
            .querySelectorAll(this.visibleItemsSelector)
            .forEach((element) => {
                element.setAttribute(
                    "highlighted-tags",
                    selectedFilters.join(",")
                );
            });
    }

    private updateContactTeasers() {
        this.removeContactTeasers();
        const visibleItems: NodeListOf<HTMLElement> =
            this.resultsContainer.querySelectorAll(this.visibleItemsSelector);
        visibleItems.forEach((item, i) => {
            if ((i + 1) % 3 === 0) {
                this.insertContactTeaserAfterItem(item);
            }
        });
        if (visibleItems.length < 3) {
            this.insertContactTeaserAfterItem(
                visibleItems[visibleItems.length - 1]
            );
        }
    }

    private removeContactTeasers() {
        const contactTeasers = this.resultsContainer.querySelectorAll(
            this.contactTeaserSelector
        );
        contactTeasers.forEach((teaser) => teaser.remove());
    }

    private insertContactTeaserAfterItem(item: HTMLElement) {
        const contactTeaser = document.createElement("div");
        contactTeaser.innerHTML = this.contactTemplateHtml;
        item.insertAdjacentElement("afterend", contactTeaser);
    }

    render() {
        return (
            <Host class={this.baseClass}>
                <div>
                    <slot name="counter" />
                </div>
                <slot name="results-container"></slot>
                <slot name="contact-template"></slot>
            </Host>
        );
    }
}
